// ---------------------------------------------------------------------------
// Step Icons — Inline SVG icons mapped to each StepId
// ---------------------------------------------------------------------------
// Uses currentColor so the icon inherits --po-color-primary from the parent.
// SVG paths inspired by lucide-react icon set.
// ---------------------------------------------------------------------------

import type { ReactNode } from 'react';
import type { StepId } from '@/api/types';

const SIZE = 20;
const COMMON: React.SVGProps<SVGSVGElement> = {
  width: SIZE,
  height: SIZE,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
};

function Icon(props: { children: ReactNode }) {
  return <svg {...COMMON}>{props.children}</svg>;
}

// ── Individual icons ──

export function Globe() {
  return (
    <Icon>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
      <path d="M2 12h20" />
    </Icon>
  );
}

function MapPin() {
  return (
    <Icon>
      <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
      <circle cx="12" cy="10" r="3" />
    </Icon>
  );
}

function Building() {
  return (
    <Icon>
      <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z" />
      <path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2" />
      <path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2" />
      <path d="M10 6h4" />
      <path d="M10 10h4" />
      <path d="M10 14h4" />
      <path d="M10 18h4" />
    </Icon>
  );
}

function Zap() {
  return (
    <Icon>
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    </Icon>
  );
}

function Landmark() {
  return (
    <Icon>
      <line x1="3" x2="21" y1="22" y2="22" />
      <line x1="6" x2="6" y1="18" y2="11" />
      <line x1="10" x2="10" y1="18" y2="11" />
      <line x1="14" x2="14" y1="18" y2="11" />
      <line x1="18" x2="18" y1="18" y2="11" />
      <polygon points="12 2 20 7 4 7" />
    </Icon>
  );
}

function CheckCircle() {
  return (
    <Icon>
      <circle cx="12" cy="12" r="10" />
      <path d="m9 12 2 2 4-4" />
    </Icon>
  );
}

// ── StepId → Icon mapping ──
// One icon per org‑type group for visual consistency.

const STEP_ICON_MAP: Partial<Record<StepId, () => ReactNode>> = {
  country: Globe,
  local_consumer_type: MapPin,
  address: MapPin,
  address_validation: MapPin,
  dso_org: Building,
  dso_main_tariff: Building,
  dso_tariff: Building,
  dso_properties: Building,
  retailer_org: Zap,
  retailer_main_tariff: Zap,
  retailer_signup_date: Zap,
  retailer_tariff: Zap,
  retailer_properties: Zap,
  retailer_custom_type: Zap,
  retailer_custom_properties: Zap,
  retailer_custom_prices: Zap,
  authorities_conditions: Landmark,
  authorities: Landmark,
  summary: CheckCircle,
};

/** Returns the icon ReactNode for a given step, or undefined if none defined. */
export function getStepIcon(stepId: StepId): ReactNode | undefined {
  const factory = STEP_ICON_MAP[stepId];
  return factory ? factory() : undefined;
}
